'use client';
/* eslint-disable tailwindcss/no-arbitrary-value */

import { FC, useCallback, useMemo, useRef, useState } from 'react';
import { useBreakpoint } from '../../../../../car-insurance/hooks/useBreakpoint';
import { InstagramEmbed } from './components/InstagramEmbed';
import { YouTubeEmbed } from './components/YouTubeEmbed';
import { useYouTubeApi } from './hooks';
import {
  extractYouTubeId,
  getAspectPadding,
  getInstagramEmbedUrl,
  isInstagramEmbed,
  isYouTubeEmbed,
} from './utils';
import {
  ComponentVideoBlock,
  EnumVideoBlockAspectRatio,
} from '$/(cms)/_modules/cmsService/types';

export const VideoBlock: FC<ComponentVideoBlock> = (props) => {
  const {
    embedType,
    aspectRatio,
    placeholderImage,
    caption,
    youtubeUrlOrId,
    instagramUrl,
    sectionTitle,
  } = props;

  const youtubeId = useMemo(
    () => extractYouTubeId(youtubeUrlOrId),
    [youtubeUrlOrId],
  );
  const instagramEmbedUrl = useMemo(() => {
    if (!isInstagramEmbed(embedType)) return '';

    return getInstagramEmbedUrl(instagramUrl);
  }, [embedType, instagramUrl]);
  const [isPlaying, setIsPlaying] = useState(false);
  const isMobile = useBreakpoint() === 'sm';
  const iframeRef = useRef<HTMLIFrameElement | null>(null);

  const aspectPadding = useMemo(
    () => getAspectPadding(embedType ? aspectRatio : undefined),
    [aspectRatio, embedType],
  );

  useYouTubeApi(embedType, isPlaying);

  const iframeSrc = useMemo(() => {
    if (!isPlaying || !youtubeId) return '';

    const params = new URLSearchParams({
      autoplay: '1',
      rel: '0',
      modestbranding: '1',
      playsinline: isMobile ? '0' : '1',
    });

    return `https://www.youtube.com/embed/${youtubeId}?${params.toString()}`;
  }, [isMobile, isPlaying, youtubeId]);

  const handlePlay = useCallback(() => {
    if (!isYouTubeEmbed(embedType) || !youtubeId) return;

    setIsPlaying(true);
  }, [embedType, youtubeId]);

  if (!embedType) return null;
  if (isYouTubeEmbed(embedType) && (!youtubeId || !placeholderImage)) {
    return null;
  }

  if (
    isInstagramEmbed(embedType) &&
    (!instagramEmbedUrl || instagramEmbedUrl.length === 0)
  ) {
    return null;
  }

  const isPortrait = aspectRatio === EnumVideoBlockAspectRatio.Ratio_9_16;

  return (
    <div className="flex flex-col gap-spacing/gap/y">
      {sectionTitle && (
        <h2 className="mhg-alpha-typography-display-1 text-text/primary">
          {sectionTitle}
        </h2>
      )}

      {isYouTubeEmbed(embedType) && placeholderImage && (
        <YouTubeEmbed
          aspectPadding={aspectPadding}
          caption={caption}
          iframeSrc={iframeSrc}
          iframeRef={iframeRef}
          isPlaying={isPlaying}
          isPortrait={isPortrait}
          onPlay={handlePlay}
          placeholderImage={placeholderImage}
          sectionTitle={sectionTitle}
        />
      )}

      {isInstagramEmbed(embedType) && instagramEmbedUrl && (
        <InstagramEmbed
          embedUrl={instagramEmbedUrl}
          sectionTitle={sectionTitle}
        />
      )}
    </div>
  );
};
